import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Gem, Check, X, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { tenureLabel } from '@/components/megatier/MegaTierTenureBadge';
import ShareToXButton from '@/components/ShareToXButton';
import { BadgeHelpIcon } from '@/components/BadgeKey';
import { megaTierShareText, MEGA_TIER_HASHTAGS } from '@/components/megatier/megaTierShareText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXPLAINER = 'Currently on the Mega Tier Undervalued Stocks list: top-20% quality + value rank, at least 4 of 5 Graham checks, Piotroski F-Score 7+. Recomputed weekly — the badge disappears if the stock drops off.';

const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);
const money = (v) => (v == null ? '—' : v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : `$${(v / 1e6).toFixed(0)}M`);

function CheckList({ items, testId }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul className="mt-1.5 space-y-1" data-testid={testId}>
      {items.map((c) => (
        <li key={c.key} className="flex items-start gap-2 text-xs">
          {c.pass
            ? <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-500 shrink-0" />
            : <X className="w-3.5 h-3.5 mt-0.5 text-rose-500 shrink-0" />}
          <span className="min-w-0">
            <span className="font-semibold text-foreground">{c.label}</span>
            {c.value && <span className="text-muted-foreground"> — {c.value}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function StageRow({ n, title, summary, children }) {
  return (
    <div className="rounded-md border border-border/60 bg-muted/20 p-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Stage {n} · {title}</span>
        <span className="font-mono text-xs font-bold text-foreground">{summary}</span>
      </div>
      {children}
    </div>
  );
}

export default function MegaTierEliteBadge({ ticker, onNavigate }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    setData(null);
    if (!ticker) return undefined;
    axios.get(`${API}/mega-tier/badge/${encodeURIComponent(ticker)}`)
      .then((r) => { if (alive) setData(r.data || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [ticker]);
  if (!data?.elite) return null;
  const d = data.detail || {};
  const tenure = tenureLabel(d.elite_since);
  const goToPage = (e) => {
    if (!onNavigate) return;
    e.preventDefault();
    setOpen(false);
    onNavigate('/mega-tier');
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300/60 dark:border-amber-700/60 hover:bg-amber-200/70 dark:hover:bg-amber-900/60 transition-colors"
        title={EXPLAINER}
        data-testid="modal-mega-tier-elite-badge"
      >
        <Gem className="w-3 h-3" /> Mega Tier Elite
      </button>
      <BadgeHelpIcon badgeKey="mega_tier_elite" testIdSuffix="modal" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="mega-tier-elite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <Gem className="w-4 h-4" /> {ticker} · Mega Tier Elite
              {tenure && <span className="ml-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-950/60" data-testid="mega-tier-elite-tenure">{tenure}</span>}
            </DialogTitle>
            <DialogDescription className="text-xs text-foreground/80">{EXPLAINER}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5 mt-1">
            <StageRow n={1} title="Combined rank" summary={d.combined_pct != null ? `top ${d.combined_pct.toFixed(1)}%` : '—'}>
              <div className="mt-1.5 grid grid-cols-3 gap-2 text-xs">
                <div><div className="text-[10px] text-muted-foreground">Earnings yield</div><div className="font-mono font-bold">{pct(d.earnings_yield)}</div></div>
                <div><div className="text-[10px] text-muted-foreground">Return on capital</div><div className="font-mono font-bold">{pct(d.roc)}</div></div>
                <div><div className="text-[10px] text-muted-foreground">Position</div><div className="font-mono font-bold">#{d.combined_position ?? '—'}</div></div>
              </div>
            </StageRow>
            <StageRow n={2} title="Graham checks" summary={`${d.graham_score ?? '—'}/5`}>
              <CheckList items={d.graham_checks} testId="mega-tier-elite-graham" />
            </StageRow>
            <StageRow n={3} title="Piotroski F-Score" summary={`${d.f_score ?? '—'}/${d.f_known ?? 9}`}>
              <CheckList items={d.f_signals} testId="mega-tier-elite-fscore" />
            </StageRow>
            <div className="grid grid-cols-3 gap-2 text-xs px-0.5">
              <div><div className="text-[10px] text-muted-foreground">NCAV / share</div><div className="font-mono font-bold">{d.ncav_per_share == null ? '—' : `$${d.ncav_per_share.toFixed(2)}`}{d.net_net && <span className="ml-1 text-[9px] text-emerald-600 dark:text-emerald-400">NET-NET</span>}</div></div>
              <div><div className="text-[10px] text-muted-foreground">Market cap</div><div className="font-mono font-bold">{money(d.market_cap)}</div></div>
              <div><div className="text-[10px] text-muted-foreground">Sector</div><div className="font-bold truncate">{d.sector || '—'}</div></div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Link
              to="/mega-tier"
              onClick={goToPage}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-bold bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-400/50 hover:bg-amber-500/25 transition-colors"
              data-testid="mega-tier-elite-open-page"
            >
              <Gem className="w-4 h-4" /> Open Mega Tier Undervalued Stocks <ArrowRight className="w-4 h-4" />
            </Link>
            <span data-testid="mega-tier-elite-share" className="inline-flex items-center justify-center rounded-md border border-border/60 px-2.5 py-2 hover:bg-muted/60 transition-colors" title="Share this breakdown on X">
              <ShareToXButton
                ticker={ticker}
                text={megaTierShareText({ ...d, ticker, stage3_pass: true })}
                hashtags={MEGA_TIER_HASHTAGS}
                size="w-4 h-4"
              />
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
